import Avatar from '@mui/material/Avatar'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import SvgIcon from '@mui/material/SvgIcon'
import React from 'react'

import { ReactComponent as ContainerIcon } from 'assets/Container.svg'
import { SERVICE_ACTION_TYPE_TO_SVG } from 'components/services/ServiceActionTypeChip'
import { SERVICE_ACTION_TYPE_TO_COLOR_MAP } from 'settings/constants/service'
import T from 'T'
import { ALPHA_38_PERCENT } from 'theme/colors'
import { getMeasureUnit } from 'utils/get-measure-unit'
import { get } from 'utils/lodash'

import type { WorkOrderContainer } from '../../containers/types'

// The action maps live in legacy .js modules; cast to string-indexable records so the
// WO's free-form action string can resolve its icon/color without implicit-any noise.
const ACTION_SVG = SERVICE_ACTION_TYPE_TO_SVG as Record<string, React.FC<React.SVGProps<SVGSVGElement>>>
const ACTION_COLOR = SERVICE_ACTION_TYPE_TO_COLOR_MAP as Record<string, Record<number, string>>

// Container chip: [action-type-icon] <size> - <serial#> [container-type-icon]. The action
// icon prefers the WO↔container link's actionType (embedded on the exception's container by
// the exception read model), falling back to the WO's service action; size/serial from the linked container
// (serialNumber preferred, barCode as legacy fallback). When the exception carries no
// container link (WO-level exception) it falls back to "Work Order".
type ExceptionContainerChipProps = {
  serviceAction?: string | null
  container?: WorkOrderContainer | null
}

const resolveSizeLabel = (container?: WorkOrderContainer | null): string => {
  const measure = get(container, 'measure')
  const sizeFromMeasure = measure ? `${get(measure, 'volume', '')} ${getMeasureUnit(get(measure, 'unit', ''))}`.trim() : ''
  return sizeFromMeasure || get(container, 'containerType.name', '') || ''
}

/** "<size> - <serial#>" display label for a WO container (chip + exception target selector). */
export const buildContainerLabel = (container?: WorkOrderContainer | null): string => {
  const sizeLabel = resolveSizeLabel(container)
  const serialNumber = get(container, 'serialNumber') || get(container, 'barCode', '')
  return [sizeLabel, serialNumber].filter(Boolean).join(' - ')
}

const ExceptionContainerChip: React.FC<ExceptionContainerChipProps> = ({ serviceAction, container }) => {
  const theme = useTheme()
  const actionKey = String(get(container, 'actionType') || serviceAction || '').toUpperCase()
  const ActionIcon = ACTION_SVG[actionKey]
  const actionColor = ACTION_COLOR[actionKey]

  const containerLabel = buildContainerLabel(container)
  const hasContainer = Boolean(container && containerLabel)

  const avatarIcon = ActionIcon ? <ActionIcon /> : <ContainerIcon />

  return (
    <Chip
      avatar={
        <Avatar sx={{ backgroundColor: `${theme.palette.common.black}${ALPHA_38_PERCENT}` }}>
          <SvgIcon inheritViewBox sx={{ fontSize: 14, color: theme.palette.common.white }}>
            {avatarIcon}
          </SvgIcon>
        </Avatar>
      }
      label={
        hasContainer ? (
          <Stack alignItems="center" direction="row" gap={0.5}>
            <span>{containerLabel}</span>
            <SvgIcon inheritViewBox sx={{ fontSize: 14 }}>
              <ContainerIcon />
            </SvgIcon>
          </Stack>
        ) : (
          T.WORK_ORDER
        )
      }
      size="small"
      sx={{ backgroundColor: hasContainer ? actionColor?.[100] : undefined, borderRadius: '4px' }}
      variant={hasContainer ? 'filled' : 'outlined'}
    />
  )
}

export default ExceptionContainerChip
